import { useEffect, useRef, useState } from 'react';
import type { Conversation } from '../types';
import {
  uploadMessageAttachment,
  isImageAttachment,
  attachmentIcon,
  MAX_ATTACHMENT_BYTES,
  type MessageAttachment,
} from '@/lib/messageAttachment';
import AiConversationView from './AiConversationView';
import AttachmentViewer, { type ViewerAttachment } from '@/components/feature/AttachmentViewer';

interface Props {
  conversation: Conversation;
  commerceId: number;
  sending: boolean;
  onSend: (text: string, attachment?: MessageAttachment | null) => Promise<boolean>;
  onBack: () => void;
}

/**
 * Fil de discussion d'un client : historique + zone de réponse.
 * Deux onglets : les messages privés et la conversation du client avec
 * l'assistant IA. Le marchand peut joindre une photo ou un document.
 */
export default function ConversationThread({ conversation, commerceId, sending, onSend, onBack }: Props) {
  const [tab, setTab] = useState<'chat' | 'ai'>('chat');
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<MessageAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState<ViewerAttachment | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tab !== 'chat') return;
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.contactId, conversation.messages.length, tab]);

  const name = conversation.contact?.name || `Client #${conversation.contactId}`;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('Fichier trop volumineux (10 Mo maximum).');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadMessageAttachment(file, commerceId);
      setAttachment(uploaded);
    } catch {
      setError("Impossible de joindre le fichier. Réessayez.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async () => {
    const text = input.trim();
    if ((!text && !attachment) || sending || uploading) return;
    setError(null);
    const ok = await onSend(text, attachment);
    if (ok) {
      setInput('');
      setAttachment(null);
    } else {
      setError("L'envoi a échoué. Réessayez.");
    }
  };

  return (
    <div className="flex flex-col flex-1 min-w-0 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden h-[calc(100vh-220px)] min-h-[420px]">
      {/* En-tête contact */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-background-200/70 bg-background-100/50 flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="lg:hidden w-8 h-8 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200/60 cursor-pointer flex-shrink-0"
          aria-label="Retour"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {conversation.contact?.image ? (
            <img src={conversation.contact.image} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-accent-700">{name.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-900 truncate">{name}</p>
          <p className="text-[11px] text-foreground-500">{conversation.messages.length} message{conversation.messages.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Onglets Messages / Assistant IA */}
      <div className="flex items-center gap-1 px-3 pt-2 border-b border-background-200/70 flex-shrink-0">
        <button
          type="button"
          onClick={() => setTab('chat')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
            tab === 'chat' ? 'bg-secondary-100 text-secondary-900' : 'text-foreground-500 hover:bg-background-100'
          }`}
        >
          <i className="ri-chat-3-line"></i>
          Messages privés
        </button>
        <button
          type="button"
          onClick={() => setTab('ai')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
            tab === 'ai' ? 'bg-accent-100 text-accent-900' : 'text-foreground-500 hover:bg-background-100'
          }`}
        >
          <i className="ri-robot-2-line"></i>
          Assistant IA
        </button>
      </div>

      {tab === 'ai' ? (
        <AiConversationView clientId={conversation.contactId} />
      ) : (
        <>
          {/* Historique */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {conversation.messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center text-foreground-400">
                <i className="ri-chat-3-line text-2xl mb-2"></i>
                <p className="text-xs">Aucun message privé pour l&apos;instant. Écrivez le premier.</p>
              </div>
            )}
            {conversation.messages.map((m) => {
              const mine = m.sender_id === commerceId;
              const hasFile = Boolean(m.attachment_url);
              const isImg = isImageAttachment(m.attachment_type, m.attachment_name);
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] px-3.5 py-2 rounded-lg text-sm ${mine ? 'bg-primary-100/80 text-foreground-800' : 'bg-background-100 text-foreground-800'}`}>
                    {m.message && <p style={{ whiteSpace: 'pre-wrap' }}>{m.message}</p>}

                    {hasFile && (
                      isImg ? (
                        <button
                          type="button"
                          onClick={() =>
                            setViewer({
                              url: m.attachment_url as string,
                              name: m.attachment_name,
                              type: m.attachment_type,
                            })
                          }
                          className={`block rounded-md overflow-hidden cursor-pointer ${m.message ? 'mt-1.5' : ''}`}
                          aria-label={`Ouvrir ${m.attachment_name || 'la pièce jointe'}`}
                        >
                          <img
                            src={m.attachment_url as string}
                            alt={m.attachment_name || 'Pièce jointe'}
                            className="max-w-[220px] max-h-[220px] w-auto h-auto object-cover rounded-md"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setViewer({
                              url: m.attachment_url as string,
                              name: m.attachment_name,
                              type: m.attachment_type,
                            })
                          }
                          className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md bg-background-50 border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer text-left w-full ${m.message ? 'mt-1.5' : ''}`}
                        >
                          <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                            <i className={attachmentIcon(m.attachment_type, m.attachment_name)}></i>
                          </span>
                          <span className="min-w-0">
                            <span className="block text-xs font-medium text-foreground-800 truncate max-w-[150px]">{m.attachment_name || 'Document'}</span>
                            <span className="block text-[10px] text-foreground-400">Aperçu dans Zifek</span>
                          </span>
                        </button>
                      )
                    )}

                    <p className="text-[10px] text-foreground-400 mt-1">
                      {new Date(m.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {/* Réponse */}
          <div className="p-3 border-t border-background-200/70 flex-shrink-0">
            {error && <p className="text-xs text-red-500 mb-2">{error}</p>}

            {attachment && (
              <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-lg bg-background-100 border border-background-200/70 w-fit max-w-full">
                <i className={`${attachmentIcon(attachment.type, attachment.name)} text-accent-600`}></i>
                <span className="text-xs text-foreground-700 truncate max-w-[200px]">{attachment.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                  aria-label="Retirer la pièce jointe"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || sending}
                className="w-10 h-10 rounded-full border border-background-200/70 text-foreground-600 flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-50 transition-colors flex-shrink-0"
                title="Joindre une photo ou un document"
              >
                <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-attachment-2'}></i>
              </button>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder={`Répondre à ${name}...`}
                className="flex-1 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <button
                type="button"
                onClick={submit}
                disabled={(!input.trim() && !attachment) || sending || uploading}
                className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors flex-shrink-0"
                aria-label="Envoyer"
              >
                <i className={sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}></i>
              </button>
            </div>
          </div>
        </>
      )}
      {viewer && <AttachmentViewer attachment={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}